import { Application, Container, Graphics, Text } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { DEMO_FORM, type MathProblem, type Notation, type Operation } from "@talent/tasks";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  untilAbandoned,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { ensureGlyphFont, numberGlyph, signGlyph, type Sign } from "./glyph.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Arithmetic — a question above, answer tiles below.
 *
 * Modelled on `choice-task.ts` deliberately: same trial loop, same layout discipline, same feedback.
 * What differs is entirely in what a tile CONTAINS — a numeral or a group of things rather than a
 * pattern of dots — so this is a new painter around a proven shell rather than a new game engine.
 *
 * **Multiple choice rather than a keypad**, and that is a decision about five-year-olds rather than
 * about arithmetic. A child who knows the answer is four and cannot yet find ۴ among ten keys has
 * been marked wrong for the wrong reason. Four large tiles ask only the thing being taught.
 *
 * **Objects before symbols.** Whether a quantity is drawn as things or as a numeral is part of the
 * level, not a rendering preference: a child counts apples before they read the symbol for how many.
 * `problem.notation` decides the question and `problem.answerNotation` the tiles, and the generator
 * never puts more than five objects on a tile — past that, two tiles are indistinguishable at a
 * glance and the trial becomes a test of eyesight.
 */

export interface MathTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateProblem: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => MathProblem;
  scoreTrial: (correctIndex: number, chosenIndex: number) => boolean;
  signal?: AbortSignal;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

/** Which sign an operation is written with. */
const SIGN_FOR: Record<Operation, Sign | null> = {
  count: null,
  add: "plus",
  subtract: "minus",
  multiply: "times",
  divide: "dividedBy",
};

export async function runMathTask(opts: RunOptions): Promise<MathTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  /**
   * The font before the first glyph.
   *
   * Pixi rasterises text on creation and caches it, so a numeral created before Vazirmatn has
   * loaded is drawn in the fallback face for the rest of the sitting.
   */
  await ensureGlyphFont();

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    backdrop.circle(w / 2, h * 0.28, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /** A rounded tile, the shared background of a quantity wherever one appears. */
  function drawTilePanel(
    g: Graphics,
    size: number,
    state: "idle" | "right" | "wrong" | "question",
  ): void {
    const panel =
      state === "right"
        ? mix(COLOR.correct, COLOR.stage, 0.55)
        : state === "wrong"
          ? mix(COLOR.wrong, COLOR.stage, 0.55)
          : mix(COLOR.stage, COLOR.card, 0.12);

    g.clear();
    g.roundRect(-size / 2, -size / 2, size, size, size * 0.2).fill(panel);
    g.roundRect(
      -size / 2 + size * 0.08,
      -size / 2 + size * 0.06,
      size * 0.84,
      size * 0.16,
      size * 0.1,
    ).fill({ color: 0xffffff, alpha: state === "question" ? 0.16 : 0.07 });
  }

  /**
   * The four things that get counted, in the order the trials cycle through them.
   *
   * **Counting four beads and then four beads again is the same picture twice.** A four-year-old
   * counting the same dots in the same colour twelve times running is being asked one question over and
   * over, and the screen looks stuck — which is what it was reported as. Changing the object changes the
   * picture without changing the skill: four is four whether they are beads, leaves or stars, and
   * noticing that is part of what counting IS.
   *
   * Four shapes and four colours advancing at different rates, so the pair does not repeat until the
   * trials do.
   */
  const SHAPES = ["bead", "leaf", "star", "block"] as const;
  const OBJECT_COLORS = [COLOR.sunny, COLOR.watermelon, COLOR.turquoise, COLOR.mint];

  /**
   * `count` things, in a shape a child can take in at a glance.
   *
   * Laid out in rows of at most three, which is what makes five readable as five rather than as a
   * line to be counted — the same reason dice pips are arranged and not queued. Never asked for more
   * than five: the generator caps object notation there.
   *
   * `variant` picks the shape and the colour. Passing `null` keeps the plain bead in the caller's own
   * colour, which is what the ANSWER tiles want — a tile is a small square of dots, and varying it
   * there would make three tiles look like three different questions.
   */
  function drawGroup(
    g: Graphics,
    count: number,
    size: number,
    color: number,
    variant: number | null = null,
  ): void {
    /**
     * Rows of five once there are more than six.
     *
     * Three or fewer sit in a line; up to six split into two rows, which is how dice pips read. Beyond
     * that the only arrangement in which a child can SEE the number rather than tally it is rows of
     * five — it is how counting past ten is taught, and twenty dots in two rows of ten is a number
     * nobody can read at a glance.
     */
    const perRow = count <= 3 ? count : count <= 6 ? Math.ceil(count / 2) : 5;
    const rows = Math.ceil(count / perRow);
    const radius = Math.min(size / (perRow * 2.6), size / (rows * 2.9));
    const shape = variant === null ? "bead" : (SHAPES[variant % SHAPES.length] as string);
    const fill =
      variant === null
        ? color
        : (OBJECT_COLORS[Math.floor(variant / SHAPES.length) % OBJECT_COLORS.length] as number);

    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / perRow);
      const inRow = Math.min(perRow, count - row * perRow);
      const x = (i % perRow) * radius * 2.6 - ((inRow - 1) * radius * 2.6) / 2;
      const y = row * radius * 2.9 - ((rows - 1) * radius * 2.9) / 2;
      drawOne(g, shape, x, y, radius, fill);
    }
  }

  /**
   * One countable thing.
   *
   * All four are drawn to the same radius and the same visual weight on purpose: a star that reads as
   * bigger than a bead would make five stars look like more than five beads, and the level is about
   * quantity rather than area.
   */
  function drawOne(
    g: Graphics,
    shape: string,
    x: number,
    y: number,
    radius: number,
    color: number,
  ): void {
    if (shape === "block") {
      g.roundRect(x - radius * 0.86, y - radius * 0.86, radius * 1.72, radius * 1.72, radius * 0.36)
        .fill(color);
      g.roundRect(x - radius * 0.56, y - radius * 0.62, radius * 0.5, radius * 0.34, radius * 0.16)
        .fill({ color: 0xffffff, alpha: 0.4 });
      return;
    }
    if (shape === "leaf") {
      // A circle pinched at two opposite corners: two arcs meeting at a point, drawn as one path.
      g.moveTo(x, y - radius)
        .quadraticCurveTo(x + radius, y - radius, x + radius, y)
        .quadraticCurveTo(x + radius, y + radius, x, y + radius)
        .quadraticCurveTo(x - radius, y + radius, x - radius, y)
        .quadraticCurveTo(x - radius, y - radius, x, y - radius)
        .fill(color);
      g.moveTo(x, y - radius * 0.7)
        .lineTo(x, y + radius * 0.7)
        .stroke({ width: Math.max(1, radius * 0.14), color: 0xffffff, alpha: 0.45 });
      return;
    }
    if (shape === "star") {
      const points: number[] = [];
      for (let p = 0; p < 10; p++) {
        // Five outer points and five inner, starting at the top so it sits upright.
        const r = p % 2 === 0 ? radius : radius * 0.46;
        const angle = -Math.PI / 2 + (p * Math.PI) / 5;
        points.push(x + Math.cos(angle) * r, y + Math.sin(angle) * r);
      }
      g.poly(points).fill(color);
      return;
    }
    g.circle(x, y, radius).fill(color);
    // Off-centre highlight: the difference between a dot and a bead.
    g.circle(x - radius * 0.3, y - radius * 0.32, radius * 0.34).fill({
      color: 0xffffff,
      alpha: 0.42,
    });
  }

  /**
   * Renders one trial. Resolves with the index the child chose.
   *
   * Layout is recomputed rather than computed once, for the reason `choice-task.ts` records: on a
   * phone the visual viewport changes size whenever the address bar slides away, which fires a
   * renderer resize — and anything positioned from the dimensions at trial start is then placed for a
   * screen that no longer exists.
   */
  function present(problem: MathProblem, variant = 0): {
    chosen: Promise<number>;
    /** Answers the question as a finger would. Used only by the demonstration. */
    simulateTap: (index: number) => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);

    const question = new Container();
    layer.addChild(question);

    /** Everything drawn inside the question, rebuilt whenever the layout changes. */
    const questionParts: (Graphics | Text)[] = [];

    const tiles = problem.options.map(() => {
      const holder = new Container();
      const panel = new Graphics();
      holder.addChild(panel);
      holder.eventMode = "static";
      holder.cursor = "pointer";
      layer.addChild(holder);
      return { holder, panel, content: [] as (Graphics | Text)[] };
    });

    let tileSize = touchMin();
    let states: ("idle" | "right" | "wrong")[] = problem.options.map(() => "idle");
    /** Set once the flashed levels have hidden the question. */
    let hidden = false;

    /** A quantity, drawn as a numeral or as things. Returned so the caller can dispose of it. */
    function drawQuantity(
      parent: Container,
      value: number,
      notation: Notation,
      size: number,
      color: number,
    ): Graphics | Text {
      if (notation === "digit") {
        const glyph = numberGlyph(value, { size: size * 0.62, color });
        parent.addChild(glyph);
        return glyph;
      }
      const g = new Graphics();
      // The question's objects change with the trial; the tiles below keep the plain bead.
      drawGroup(g, value, size * 0.78, color, variant);
      parent.addChild(g);
      return g;
    }

    function layoutQuestion(w: number, top: number, height: number): void {
      for (const part of questionParts) part.destroy();
      questionParts.length = 0;
      question.removeChildren();

      question.position.set(w / 2, top + height / 2);
      if (hidden) {
        /**
         * The flashed level, after the question has gone.
         *
         * An empty well rather than a blank space, and rather than a numeral or a word: the child has
         * to answer from what they saw, and a hole says "it was here" without saying anything else.
         */
        const well = new Graphics();
        const size = Math.min(height * 0.8, 160);
        well
          .roundRect(-size / 2, -size / 2, size, size, size * 0.22)
          .fill({ color: 0x000000, alpha: 0.22 });
        well
          .roundRect(-size / 2, -size / 2, size, size, size * 0.22)
          .stroke({ width: 3, color: accent, alpha: 0.7 });
        question.addChild(well);
        questionParts.push(well);
        return;
      }

      const sign = SIGN_FOR[problem.operation];
      const cell = Math.min(height * 0.86, w * 0.26, 150);
      const signSize = cell * 0.5;
      const gap = cell * 0.22;

      /**
       * Laid out right to left, because the document is.
       *
       * Arithmetic is written left to right inside a Persian text — `۳ + ۲` reads with the three on
       * the left — so the FIRST operand goes leftmost and the layout is a plain left-to-right row.
       * Stated because the sequence game does the opposite for its own good reason, and a reader
       * comparing the two files should find the difference explained rather than looking like a bug.
       */
      type Piece = { kind: "value"; value: number } | { kind: "sign"; sign: Sign };
      const pieces: Piece[] = sign
        ? [
            { kind: "value", value: problem.operands[0] as number },
            { kind: "sign", sign },
            { kind: "value", value: problem.operands[1] as number },
          ]
        : [{ kind: "value", value: problem.operands[0] as number }];

      const totalW =
        pieces.reduce((sum, piece) => sum + (piece.kind === "value" ? cell : signSize), 0) +
        gap * (pieces.length - 1);

      let x = -totalW / 2;
      for (const piece of pieces) {
        if (piece.kind === "value") {
          const holder = new Container();
          holder.position.set(x + cell / 2, 0);
          const panel = new Graphics();
          drawTilePanel(panel, cell, "question");
          holder.addChild(panel);
          question.addChild(holder);
          questionParts.push(panel);
          questionParts.push(drawQuantity(holder, piece.value, problem.notation, cell, accent));
          x += cell + gap;
        } else {
          const glyph = signGlyph(piece.sign, { size: signSize, color: COLOR.cream });
          glyph.position.set(x + signSize / 2, 0);
          question.addChild(glyph);
          questionParts.push(glyph);
          x += signSize + gap;
        }
      }
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const pad = Math.max(12, w * 0.045);
      const gap = Math.max(10, w * 0.03);
      const usableW = w - pad * 2;
      const count = problem.options.length;

      const questionH = Math.min(h * 0.3, 190);

      // Columns from the touch minimum rather than the reverse: computing a size and clamping it up
      // is what pushes four tiles off a narrow screen.
      const fitting = Math.max(1, Math.floor((usableW + gap) / (touchMin() + gap)));
      const columns =
        fitting >= count ? count : count === 4 ? 2 : Math.max(2, Math.min(fitting, count));
      const rows = Math.ceil(count / columns);
      const availableH = h - questionH - pad * 2 - gap;

      tileSize = Math.min(
        (usableW - gap * (columns - 1)) / columns,
        (availableH - gap * (rows - 1)) / rows,
      );

      layoutQuestion(w, pad, questionH);

      const gridH = tileSize * rows + gap * (rows - 1);
      const gridTop = pad + questionH + gap + (availableH - gridH) / 2;

      tiles.forEach((tile, i) => {
        const col = i % columns;
        const row = Math.floor(i / columns);
        const inRow = Math.min(columns, count - row * columns);
        const rowW = tileSize * inRow + gap * (inRow - 1);
        const x = (w - rowW) / 2 + col * (tileSize + gap) + tileSize / 2;
        const y = gridTop + row * (tileSize + gap) + tileSize / 2;
        tile.holder.position.set(x, y);

        for (const part of tile.content) part.destroy();
        tile.content.length = 0;
        drawTilePanel(tile.panel, tileSize, states[i] ?? "idle");

        const state = states[i] ?? "idle";
        const ink =
          state === "right" ? COLOR.correct : state === "wrong" ? COLOR.wrong : accent;
        const value = problem.options[i] as number;
        if (problem.answerNotation === "digit") {
          const glyph = numberGlyph(value, { size: tileSize * 0.56, color: ink });
          tile.holder.addChild(glyph);
          tile.content.push(glyph);
        } else {
          const g = new Graphics();
          drawGroup(g, value, tileSize * 0.72, ink);
          tile.holder.addChild(g);
          tile.content.push(g);
        }

        // Padded hit area: a mis-aimed tap is a motor artefact and must not be scored as a wrong
        // answer. Recomputed with the size, or a resize leaves the hit area behind.
        const half = tileSize / 2 + tileSize * 0.12;
        tile.holder.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
      });
    }

    layout();
    app.renderer.on("resize", layout);

    /**
     * The one flashed level: the question disappears after a beat.
     *
     * Recognising four things AS four is a different skill from counting them, and it cannot be
     * taught with the dots left on screen — a child will always count them, correctly, and learn
     * nothing about subitising.
     */
    if (problem.flashMs > 0) {
      void idle(problem.flashMs).then(() => {
        hidden = true;
        layout();
      });
    }

    let resolveChoice: ((index: number) => void) | null = null;
    let accepting = true;

    tiles.forEach((tile, i) => {
      tile.holder.on("pointertap", () => {
        if (!accepting || !resolveChoice) return;
        accepting = false;
        cellTone(i * 2, { volume: 0.13 });
        ripple(fx, tile.holder.position.x, tile.holder.position.y, accent, tileSize);
        resolveChoice(i);
      });

      if (!prefersReducedMotion()) {
        tile.holder.scale.set(0);
        void idle(110 + i * 70).then(() =>
          animate(300, (t) => tile.holder.scale.set(easeOutBack(t))),
        );
      }
    });

    tick();

    const chosen = new Promise<number>((resolve) => {
      resolveChoice = resolve;
    }).then(async (index) => {
      const correct = index === problem.correctIndex;
      const picked = tiles[index];

      if (correct) {
        successChime();
        states = states.map((_, i) => (i === index ? "right" : "idle"));
        layout();
        if (picked) {
          burst(fx, picked.holder.position.x, picked.holder.position.y, [
            accent,
            lighten(accent, 0.4),
            COLOR.correct,
          ]);
          await pop(picked.holder, { scale: 1.22 });
        }
        await idle(360);
      } else {
        missTone();
        states = states.map((_, i) => (i === index ? "wrong" : "idle"));
        layout();
        if (picked) await nudge(picked.holder);
        /**
         * The right answer is shown, and on the flashed level the question comes back with it.
         *
         * This is the trial the whole game exists for (R-5). A child who got it wrong and is shown
         * only that they were wrong has been tested; a child shown the answer beside the question has
         * been taught.
         */
        hidden = false;
        states = states.map((_, i) =>
          i === index ? "wrong" : i === problem.correctIndex ? "right" : "idle",
        );
        layout();
        const answer = tiles[problem.correctIndex];
        if (answer) {
          glow(
            glowLayer,
            answer.holder.position.x,
            answer.holder.position.y,
            tileSize * 0.6,
            COLOR.correct,
            700,
          );
          cellTone(4, { volume: 0.09 });
          await pop(answer.holder, { scale: 1.16 });
        }
        await idle(760);
      }
      return index;
    });

    return {
      chosen,
      /**
       * Lets the demonstration answer the question itself.
       *
       * Goes through the same tap path a finger does, so what a child watches is exactly what happens
       * when they do it — a demonstration that took a shortcut would be showing them a different game.
       */
      simulateTap: (index: number) => {
        if (!accepting || !resolveChoice) return;
        accepting = false;
        cellTone(index * 2, { volume: 0.13 });
        const tile = tiles[index];
        if (tile) ripple(fx, tile.holder.position.x, tile.holder.position.y, accent, tileSize);
        resolveChoice(index);
      },
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  /**
   * The wordless demonstration: one easy question, answered in front of the child.
   *
   * Every other game here demonstrates itself, and these two shipped without it — the props were
   * accepted and ignored, which is the worst kind of gap because the interface said otherwise.
   *
   * Drawn from `DEMO_FORM`, which is deliberately absent from every task's `formIds`, so the child is
   * never shown the answer to a scored trial they have not taken yet. At `minDifficulty`, because a
   * demonstration should be of the rule and not of the difficulty.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const problem = opts.generateProblem(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(problem);
      // A beat to look at the question before anything moves.
      await idle(1000);
      if (!opts.signal?.aborted) {
        shown.simulateTap(problem.correctIndex);
        await untilAbandoned(shown.chosen, opts.signal);
        // Held until the child taps, rather than advancing on a timer: the pace belongs to the
        // slowest child rather than to a delay somebody picked.
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: MathTaskResult["reason"] = "completed";

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();
    for (;;) {
      const problem = opts.generateProblem(task, formId, trialIndex, difficulty);

      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        /**
         * The operands, then the answer.
         *
         * The stimulus field is a `number[]`, and packing the question into it this way is what keeps
         * a trial re-scorable from the record alone years later — the alternative is knowing which
         * generator version produced it.
         */
        stimulus: [...problem.operands, problem.answer],
        options: problem.options.map((value) => [value]),
        correctIndex: problem.correctIndex,
      });
      recorder.setInTrial(true);

      const startedAt = performance.now();
      const trial = present(problem, trialIndex);
      const chosenIndex = await untilSettled(trial.chosen, opts.signal, task.measurement.trialTimeoutMs);
      if (chosenIndex === ABANDONED || chosenIndex === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (chosenIndex === TIMED_OUT) {
          await recorder.record("trial_timeout", {
            trialIndex,
            elapsedMs: performance.now() - startedAt,
          });
        }
        trial.cleanup();
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      const responseMs = performance.now() - startedAt;
      trial.cleanup();

      const correct = opts.scoreTrial(problem.correctIndex, chosenIndex);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: [problem.options[chosenIndex] ?? -1],
        correct,
        responseMs,
      });

      const speed = detectImplausibleSpeed(responseMs, problem.options.length);
      if (speed) await recorder.record("input_anomaly", speed);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
